// TeamsPage.jsx
import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  RefreshCcw,
  Building2,
  Search,
  X,
  Loader2,
  MapPin,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import TeamTable from "./TeamTable";
import LoadingScreen from "../Utils/LoadingScreen";
import { useGlobalContext } from "../../GlobalContext";

// Reuse your existing modals
import SendNotificationModal from "../Driver/SendNotificationModal";
import BulkSendNotificationModal from "../Driver/BulkSendNotificationModal";
import TablePagination from "../Utils/TablePagination";
import useTeams from "../../Sevices/useTeams";
import useShop from "../../Sevices/useShop";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";
import useDriver from "../../Sevices/Driver Api/useDriver";

export default function TeamsPage() {
  const { loading, getPincodeDetails, createDriverTeamV2, assignShopToTeam } =
    useTeams();

  const { toggleDutyStatus } = useDriver();

  const {
    teams,
    fetchDriverTeams,
    teamMembersByTeamId,
    teamMembersLoadingId,
    fetchDriverTeamListByTeamId,
  } = useDriverTeams();

  const { baseurl, token, accessToken, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const { shops, fetchShops } = useShop({
    baseurl,
    accessToken,
    token,
    AdminId,
    setIsUnauthorised,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Shops (assign one-by-one)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeam, setAssignTeam] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignWorkingCode, setAssignWorkingCode] = useState(""); // show spinner per-row

  // Notifications
  const [singleNotifOpen, setSingleNotifOpen] = useState(false);
  const [singleNotifDriver, setSingleNotifDriver] = useState(null);
  const [bulkNotifOpen, setBulkNotifOpen] = useState(false);
  const [bulkDrivers, setBulkDrivers] = useState([]);

  // Duty toggle (local state cache)
  const [dutyWorkingId, setDutyWorkingId] = useState(null);
  const [dutyMap, setDutyMap] = useState(new Map()); // driverId -> "on_duty"/"off_duty"

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [accuracy, setAccuracy] = useState("");

  // New V2 pincode/area team flow
  const [showCreatePincodeModal, setShowCreatePincodeModal] = useState(false);
  const [pincodeTeamForm, setPincodeTeamForm] = useState({
    pinCode: "",
    address: "",
    teamName: "",
    minimumDrivers: "",
    maximumDrivers: "",
    accuracy: "",
  });
  const [pincodeSearch, setPincodeSearch] = useState("");
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [pincodeSearchLoading, setPincodeSearchLoading] = useState(false);
  const [pincodeDropdownOpen, setPincodeDropdownOpen] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [openAssignDropdownTeamId, setOpenAssignDropdownTeamId] =
    useState(null);

  const teamsPerPage = 15;

  const filteredTeams = useMemo(() => {
    if (!Array.isArray(teams)) return [];

    return teams.filter(
      (team) =>
        (!teamStatusFilter || team.status === teamStatusFilter) &&
        (search === "" ||
          team.team_name?.toLowerCase().includes(search.toLowerCase()) ||
          team.teamName?.toLowerCase().includes(search.toLowerCase()) ||
          team.pinCode?.toLowerCase().includes(search.toLowerCase()) ||
          team.pin_code?.toLowerCase().includes(search.toLowerCase()) ||
          team.pincode?.toLowerCase().includes(search.toLowerCase()) ||
          team.address?.toLowerCase().includes(search.toLowerCase()) ||
          team.team_address?.toLowerCase().includes(search.toLowerCase()) ||
          team.createdOn?.toLowerCase().includes(search.toLowerCase())),
    );
  }, [teams, teamStatusFilter, search]);

  const indexOfLast = currentPage * teamsPerPage;
  const indexOfFirst = indexOfLast - teamsPerPage;
  const currentTeams = filteredTeams.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    fetchDriverTeams();
    fetchShops();
  }, []);

  useEffect(() => {
    if (!showCreatePincodeModal) return;

    const word = pincodeSearch.trim();

    if (!word) {
      setPincodeOptions([]);
      setPincodeDropdownOpen(false);
      return;
    }

    if (
      selectedPincode &&
      getPincodeLabel(selectedPincode) === word &&
      pincodeTeamForm.pinCode
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      setPincodeSearchLoading(true);

      try {
        const data = await getPincodeDetails({
          word,
          pageSize: 20,
          nextToken: "",
          previousToken: "",
        });

        const options = extractPincodeOptions(data);
        setPincodeOptions(options);
        setPincodeDropdownOpen(true);
      } finally {
        setPincodeSearchLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [
    pincodeSearch,
    showCreatePincodeModal,
    selectedPincode,
    pincodeTeamForm.pinCode,
  ]);

  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);

    try {
      await fetchDriverTeams(true);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePincodeTeamChange = (field, value) => {
    setPincodeTeamForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetPincodeTeamForm = () => {
    setPincodeTeamForm({
      pinCode: "",
      address: "",
      teamName: "",
      minimumDrivers: "",
      maximumDrivers: "",
      accuracy: "",
    });
    setPincodeSearch("");
    setPincodeOptions([]);
    setPincodeDropdownOpen(false);
    setSelectedPincode(null);
  };

  const handlePincodeSearchChange = (value) => {
    const numericValue = value.replace(/\D/g, "");

    setPincodeSearch(numericValue);
    setSelectedPincode(null);
    setPincodeDropdownOpen(Boolean(numericValue));
    handlePincodeTeamChange("pinCode", "");
  };

  const handleSelectPincode = (item) => {
    const pincode = getPincodeLabel(item);

    if (!pincode) {
      toast.error("Pincode not found");
      return;
    }

    setSelectedPincode(item);
    setPincodeSearch(pincode);
    setPincodeOptions([]);
    setPincodeDropdownOpen(false);

    const pincodeId = getPincodeId(item);

    if (!pincodeId) {
      toast.error("Pincode ID not found");
      return;
    }

    // createDriverTeam V2 expects the selected pincode record ID.
    // Auto-fill the selected pincode address, but keep the field editable.
    const selectedAddress = getPincodeAddress(item);

    setPincodeTeamForm((prev) => ({
      ...prev,
      pinCode: String(pincodeId),
      address: selectedAddress || prev.address,
    }));
  };

  const handleCreatePincodeTeam = async () => {
    if (!selectedPincode || !pincodeTeamForm.pinCode) {
      toast.error("Please select a pincode from the list");
      return;
    }

    await createDriverTeamV2({
      ...pincodeTeamForm,
      onSuccess: async () => {
        setShowCreatePincodeModal(false);
        resetPincodeTeamForm();
        await fetchDriverTeams(true);
      },
    });
  };

  const toggleExpand = async (id) => {
    const isAlreadyOpen = expanded.has(id);

    if (!isAlreadyOpen) {
      await fetchDriverTeamListByTeamId(id);
    }

    setExpanded((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  };

  // ---------- Notifications ----------
  const openSingleNotification = (driver, team) => {
    // Map team driver shape -> modal expected shape
    const mapped = {
      driverId: driver.driver_code || driver.id, // use driver_code as ID
      fullName:
        driver.fullName ||
        `${driver.first_name || ""} ${driver.last_name || ""}`.trim(),
      username: driver.username || "",
      status: driver.status || "",
      teamName: team?.team_name || "",
    };
    setSingleNotifDriver(mapped);
    setSingleNotifOpen(true);
  };

  const openBulkNotification = async (team) => {
    let memberData = teamMembersByTeamId[team.id];

    if (!memberData) {
      memberData = await fetchDriverTeamListByTeamId(team.id);
    }

    const combinedMembers = [
      ...(memberData?.primaryTeamDrivers || []),
      ...(memberData?.secondaryTeamDrivers || []),
    ];

    const mapped = combinedMembers.map((d) => ({
      driverId: d.driver_code || d.id,
      fullName:
        d.fullName || `${d.first_name || ""} ${d.last_name || ""}`.trim(),
      username: d.username || "",
      status: d.driverStatus || d.status || "",
      teamName: team.team_name,
    }));

    setBulkDrivers(mapped);
    setBulkNotifOpen(true);
  };

  // ---------- Duty toggle (from DriversPage logic) ----------
  const handleToggleDutyStatus = async (driverCode) => {
    const current = dutyMap.get(driverCode) || "off_duty";
    const next = current === "on_duty" ? "off_duty" : "on_duty";

    setDutyWorkingId(driverCode);
    setDutyMap((prev) => new Map(prev).set(driverCode, next));

    await toggleDutyStatus({ driverId: driverCode }, current === "on_duty");
    setDutyWorkingId(null);
  };

  // ---------- Assign Shops (single per call) ----------
  const openAssignShops = async (team) => {
    setAssignTeam(team);
    if (shops.length === 0) await fetchShops();
    setAssignSearch("");
    setAssignOpen(true);
  };

  const assignOneShop = async (shop) => {
    if (!assignTeam) return;

    const code = shop.shop_code;

    const curTeamName = (shop.teamName || "").trim();

    const isElsewhere =
      curTeamName &&
      curTeamName.toLowerCase() !== (assignTeam.team_name || "").toLowerCase();

    if (isElsewhere) {
      toast.error(`Already assigned to ${curTeamName}`);
      return;
    }

    const alreadyInThisTeam = (assignTeam.shopList || []).some(
      (s) => s.shop_code === code,
    );

    if (alreadyInThisTeam) {
      toast.info("This shop is already mapped to the same team.");
      return;
    }

    setAssignWorkingCode(code);

    try {
      await assignShopToTeam({
        shopCode: shop.shop_code,
        teamId: assignTeam.id,
        onSuccess: async () => {
          await fetchDriverTeams(true);

          setAssignTeam((prev) =>
            prev
              ? {
                  ...prev,
                  shopList: [
                    ...(prev.shopList || []),
                    {
                      id: shop.id,
                      shop_code: shop.shop_code,
                      shop_name: shop.shop_name,
                      shop_address: shop.shop_address,
                      shop_phone: shop.shop_phone,
                      onboardedOn: shop.onboardedOn,
                      areaid: shop.areaid,
                    },
                  ],
                }
              : prev,
          );
        },
      });
    } finally {
      setAssignWorkingCode("");
    }
  };

  const assignShopFromDropdown = async (team, shopCode) => {
    if (!team || !shopCode) return;

    const shop = shops.find((s) => String(s.shop_code) === String(shopCode));
    if (!shop) {
      toast.error("Shop not found");
      return;
    }

    setAssignWorkingCode(shop.shop_code);

    try {
      await assignShopToTeam({
        shopCode: shop.shop_code,
        teamId: team.id,
        onSuccess: async () => {
          setOpenAssignDropdownTeamId(null);
          await fetchDriverTeams(true);
        },
      });
    } finally {
      setAssignWorkingCode("");
    }
  };

  const filteredShopsForAssign = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    const list = shops || [];
    if (!q) return list;
    return list.filter(
      (s) =>
        s.shop_name?.toLowerCase().includes(q) ||
        s.shop_code?.toLowerCase().includes(q) ||
        s.shop_address?.toLowerCase().includes(q),
    );
  }, [shops, assignSearch]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(filteredTeams.length / teamsPerPage));

    if (currentPage > total) {
      setCurrentPage(total);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [filteredTeams.length, teamsPerPage, currentPage]);

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      <Toaster position="top-right" />
      <div className="sticky top-0 z-30 pb-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">Teams</h1>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          {/*  Search & Filters (left) */}
          <div className="flex flex-wrap items-end gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team, pincode, address or date..."
              className="px-2 py-1 border rounded text-xs shadow-sm w-[180px]"
            />
            <select
              value={teamStatusFilter}
              onChange={(e) => setTeamStatusFilter(e.target.value)}
              className="text-xs border rounded px-2 py-1 shadow-sm w-[130px]"
            >
              <option value="">All Status</option>
              <option value="publish">Publish</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/*  Actions (right) */}
          <div className="flex flex-wrap items-end gap-2">
            <button
              className="flex items-center space-x-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() => setShowCreatePincodeModal(true)}
            >
              <MapPin size={14} />
              <span>Create Pincode Team</span>
            </button>

            <button
              className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCcw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>
      </div>

      {teams.length === 0 ? (
        <LoadingScreen />
      ) : (
        <>
          <div className="flex-1 min-h-0 border rounded-md shadow-sm overflow-hidden">
            <div className="h-full overflow-y-auto overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-orange-400 text-left text-sm font-semibold text-white">
                    <th className="bg-orange-400 px-3 py-3 w-14 text-center">
                      #
                    </th>
                    <th className="bg-orange-400 px-4 py-3">Team</th>
                    <th className="bg-orange-400 px-4 py-3">
                      Coverage / Rules
                    </th>
                    <th className="bg-orange-400 px-4 py-3">Members / Shops</th>
                    <th className="bg-orange-400 px-4 py-3">Status</th>
                    <th className="bg-orange-400 px-4 py-3 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentTeams.map((team) => {
                    const isOpen = expanded.has(team.id);
                    const memberData = teamMembersByTeamId[team.id] || {
                      primaryTeamDrivers: [],
                      secondaryTeamDrivers: [],
                      activeTeamDrivers: [],
                    };

                    const members = [
                      ...(memberData.primaryTeamDrivers || []).map((d) => ({
                        ...d,
                        memberType: "primary",
                      })),
                      ...(memberData.secondaryTeamDrivers || []).map((d) => ({
                        ...d,
                        memberType: "secondary",
                      })),
                    ];

                    const activeDrivers = memberData.activeTeamDrivers || [];
                    const activeDriverCodeSet = new Set(
                      activeDrivers.map((d) => d.driver_code).filter(Boolean),
                    );
                    const shopsMapped = Array.isArray(team.shopList)
                      ? team.shopList
                      : [];

                    return (
                      <TeamTable
                        key={team.id}
                        team={team}
                        isOpen={isOpen}
                        members={members}
                        activeDrivers={activeDrivers}
                        activeDriverCodeSet={activeDriverCodeSet}
                        memberLoading={teamMembersLoadingId === team.id}
                        shops={shopsMapped}
                        allShops={shops}
                        openAssignDropdownTeamId={openAssignDropdownTeamId}
                        assignWorkingCode={assignWorkingCode}
                        dutyMap={dutyMap}
                        dutyWorkingId={dutyWorkingId}
                        onToggleDuty={handleToggleDutyStatus}
                        onToggle={() => toggleExpand(team.id)}
                        onOpenAssign={() => openAssignShops(team)}
                        onToggleAssignDropdown={() => {
                          setOpenAssignDropdownTeamId((prev) =>
                            prev === team.id ? null : team.id,
                          );
                        }}
                        onAssignShopFromDropdown={(shopCode) =>
                          assignShopFromDropdown(team, shopCode)
                        }
                        onOpenSingleNotif={(driver) =>
                          openSingleNotification(driver, team)
                        }
                        onOpenBulkNotif={() => openBulkNotification(team)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalCount={filteredTeams.length}
            pageSize={teamsPerPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Create Pincode Team Modal - New V2 API */}
      {showCreatePincodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Create Pincode Team
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Create a team with pincode coverage and driver capacity rules.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowCreatePincodeModal(false);
                  resetPincodeTeamForm();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={pincodeTeamForm.teamName}
                    onChange={(e) =>
                      handlePincodeTeamChange("teamName", e.target.value)
                    }
                    className="w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    placeholder="Enter team name"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pincode <span className="text-red-500">*</span>
                  </label>

                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={pincodeSearch}
                      onChange={(e) =>
                        handlePincodeSearchChange(e.target.value)
                      }
                      onFocus={() => {
                        if (pincodeSearch && !selectedPincode) {
                          setPincodeDropdownOpen(true);
                        }
                      }}
                      className="w-full border px-3 py-2 pr-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      placeholder="Type pincode to search"
                    />

                    {pincodeSearchLoading && (
                      <Loader2
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                      />
                    )}
                  </div>

                  {selectedPincode && pincodeTeamForm.pinCode && (
                    <p className="mt-1 text-[11px] text-emerald-600">
                      Selected pincode: {pincodeTeamForm.pinCode}
                    </p>
                  )}

                  {pincodeDropdownOpen && !selectedPincode && (
                    <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-lg border bg-white shadow-lg">
                      {pincodeSearchLoading ? (
                        <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-gray-500">
                          <Loader2 size={14} className="animate-spin" />
                          Searching pincodes...
                        </div>
                      ) : pincodeOptions.length > 0 ? (
                        pincodeOptions.map((item, index) => {
                          const optionId = getPincodeId(item);
                          const optionLabel = getPincodeLabel(item);
                          const detailRows = getPincodeDetailRows(item);

                          return (
                            <button
                              key={optionId || `${optionLabel}-${index}`}
                              type="button"
                              onClick={() => handleSelectPincode(item)}
                              className="w-full border-b px-3 py-3 text-left last:border-b-0 hover:bg-emerald-50"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <MapPin
                                      size={14}
                                      className="mt-0.5 shrink-0 text-emerald-600"
                                    />
                                    <span className="text-sm font-semibold text-gray-900">
                                      {optionLabel}
                                    </span>
                                  </div>
                                </div>

                                {optionId && (
                                  <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                    ID: {optionId}
                                  </span>
                                )}
                              </div>

                              {detailRows.length > 0 && (
                                <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-lg bg-gray-50 p-2.5 sm:grid-cols-2">
                                  {detailRows.map(({ key, label, value }) => (
                                    <div
                                      key={key}
                                      className="min-w-0 text-[11px] leading-4"
                                    >
                                      <span className="font-medium text-gray-500">
                                        {label}:
                                      </span>{" "}
                                      <span className="break-words text-gray-800">
                                        {value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </button>
                          );
                        })
                      ) : pincodeSearch ? (
                        <div className="px-3 py-4 text-center text-xs text-gray-500">
                          No pincode found
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={pincodeTeamForm.address}
                    onChange={(e) =>
                      handlePincodeTeamChange("address", e.target.value)
                    }
                    className="w-full border px-3 py-2 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    placeholder="Enter coverage address / area"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Drivers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={pincodeTeamForm.minimumDrivers}
                    onChange={(e) =>
                      handlePincodeTeamChange("minimumDrivers", e.target.value)
                    }
                    className="w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    placeholder="e.g. 2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Drivers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={pincodeTeamForm.maximumDrivers}
                    onChange={(e) =>
                      handlePincodeTeamChange("maximumDrivers", e.target.value)
                    }
                    className="w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    placeholder="e.g. 10"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accuracy <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={pincodeTeamForm.accuracy}
                    onChange={(e) =>
                      handlePincodeTeamChange("accuracy", e.target.value)
                    }
                    className="w-full border px-3 py-2 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="">Select accuracy</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2 border-t pt-4">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setShowCreatePincodeModal(false);
                    resetPincodeTeamForm();
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={handleCreatePincodeTeam}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <MapPin size={15} />
                      Create Pincode Team
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Shops Modal (single assign per click) */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Assign Shops</h3>
                <p className="text-xs text-gray-500">
                  Team:{" "}
                  <span className="font-medium">{assignTeam?.team_name}</span>
                </p>
              </div>
              <button
                onClick={() => setAssignOpen(false)}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5">
              <div className="mb-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search size={16} />
                  </span>
                  <input
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                    placeholder="Search shop by name, code, or address..."
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="max-h-[60vh] overflow-y-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Shop</th>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Address</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Team (current)</th>
                      <th className="px-3 py-2 text-left w-40">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShopsForAssign.map((s) => {
                      const assignedTo = s.teamName?.trim() || "";
                      const assignedElsewhere =
                        assignedTo &&
                        assignedTo.toLowerCase() !==
                          (assignTeam?.team_name || "").toLowerCase();
                      const alreadyInThisTeam = (
                        assignTeam?.shopList || []
                      ).some((x) => x.shop_code === s.shop_code);
                      const disabled = assignedElsewhere || alreadyInThisTeam;
                      const disabledReason = assignedElsewhere
                        ? `Assigned to: ${assignedTo}`
                        : alreadyInThisTeam
                          ? "Already in this team"
                          : "";

                      return (
                        <tr key={s.id} className="border-t">
                          <td className="px-3 py-2 font-medium">
                            {s.shop_name}
                          </td>
                          <td className="px-3 py-2">{s.shop_code}</td>
                          <td className="px-3 py-2">
                            <div className="truncate max-w-[360px]">
                              {s.shop_address}
                            </div>
                          </td>
                          <td className="px-3 py-2">{s.shop_phone}</td>
                          <td className="px-3 py-2">
                            {assignedTo || "—"}
                            {assignedElsewhere && (
                              <span className="ml-2 text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                Assigned to: {assignedTo}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-white text-xs ${
                                disabled
                                  ? "bg-gray-300 cursor-not-allowed"
                                  : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                              disabled={
                                disabled || assignWorkingCode === s.shop_code
                              }
                              onClick={() => assignOneShop(s)}
                              title={disabledReason}
                            >
                              {assignWorkingCode === s.shop_code ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  Assigning...
                                </>
                              ) : (
                                <>
                                  <Building2 size={14} />
                                  Assign
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredShopsForAssign.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="text-center text-gray-500 py-8"
                        >
                          No shops found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setAssignOpen(false)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modals */}
      <SendNotificationModal
        isOpen={singleNotifOpen}
        onClose={() => setSingleNotifOpen(false)}
        driver={singleNotifDriver}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
      />

      <BulkSendNotificationModal
        isOpen={bulkNotifOpen}
        onClose={() => setBulkNotifOpen(false)}
        drivers={bulkDrivers}
        teams={teams}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
        defaultTeam=""
        defaultStatus=""
      />
    </div>
  );
}

function extractPincodeOptions(data) {
  const findArray = (value, depth = 0) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object" || depth > 3) return null;

    const keys = [
      "response",
      "data",
      "items",
      "list",
      "records",
      "result",
      "results",
      "pincodeDetails",
      "pincodes",
      "pinCodes",
    ];

    for (const key of keys) {
      const found = findArray(value[key], depth + 1);
      if (found) return found;
    }

    return null;
  };

  return findArray(data) || [];
}

function getPincodeId(item) {
  return (
    item?.id ??
    item?.pincodeId ??
    item?.pinCodeId ??
    item?.pincode_id ??
    item?.pin_code_id ??
    item?.pk_pincode_id ??
    item?.pkPincodeId ??
    item?.pk_id ??
    ""
  );
}

function getPincodeLabel(item) {
  const value =
    item?.pincode ??
    item?.pinCode ??
    item?.pin_code ??
    item?.code ??
    item?.postalCode ??
    item?.postal_code ??
    item?.value ??
    item?.name ??
    getPincodeId(item);

  return String(value ?? "");
}

function getPincodeAddress(item) {
  if (!item || typeof item !== "object") return "";

  // Prefer a ready-made address/locality value from the API when available.
  const directAddress =
    item?.address ??
    item?.fullAddress ??
    item?.full_address ??
    item?.areaAddress ??
    item?.area_address ??
    item?.locality ??
    item?.area ??
    item?.areaName ??
    item?.area_name ??
    item?.officeName ??
    item?.office_name ??
    item?.postOffice ??
    item?.post_office ??
    "";

  if (String(directAddress || "").trim()) {
    return String(directAddress).trim();
  }

  // Fallback: build a useful address from the location fields returned by the API.
  const parts = [
    item?.localityName ?? item?.locality_name,
    item?.block ?? item?.taluk ?? item?.tehsil,
    item?.city ?? item?.cityName ?? item?.city_name,
    item?.district ?? item?.districtName ?? item?.district_name,
    item?.state ?? item?.stateName ?? item?.state_name,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return [...new Set(parts)].join(", ");
}

function getPincodeDetailRows(item) {
  if (!item || typeof item !== "object") return [];

  const hiddenKeys = new Set([
    "id",
    "pincodeid",
    "pinCodeId".toLowerCase(),
    "pincode_id",
    "pin_code_id",
    "pk_pincode_id",
    "pkPincodeId".toLowerCase(),
    "pk_id",
    "pincode",
    "pinCode".toLowerCase(),
    "pin_code",
    "code",
    "postalCode".toLowerCase(),
    "postal_code",
  ]);

  return Object.entries(item)
    .filter(([key, value]) => {
      if (hiddenKeys.has(String(key).toLowerCase())) return false;
      if (value === null || value === undefined || value === "") return false;
      return ["string", "number", "boolean"].includes(typeof value);
    })
    .map(([key, value]) => ({
      key,
      label: formatPincodeDetailLabel(key),
      value: String(value),
    }));
}

function formatPincodeDetailLabel(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
